import { Component, ErrorInfo, ReactNode } from 'react';
import * as Sentry from '@sentry/react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  /**
   * ZLAYT-1485: when this value changes while the fallback is showing, the
   * children are rendered again (e.g. the user picked another document
   * version). A change while nothing has crashed does nothing, so the
   * children keep their state; use this instead of `key` on the boundary.
   */
  resetKey?: unknown;
}

interface State {
  hasError: boolean;
}

/**
 * App-level error boundary.
 *
 * Without one, any unhandled render-time error in the React tree blanks
 * the entire app to a white screen. This component catches the error,
 * reports it to Sentry, and shows a minimal recovery UI with a Reload
 * button so the user can get back to a working state.
 *
 * Error boundaries cannot be implemented with hooks — React requires the
 * class-component lifecycle for getDerivedStateFromError + componentDidCatch.
 */
class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(_error: Error): State {
    return { hasError: true };
  }

  componentDidUpdate(prevProps: Props): void {
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false });
    }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Always log so developers see it in the browser console even if Sentry
    // is not wired up in the current environment (e.g. local dev).
    console.error('[ErrorBoundary] uncaught render error:', error, errorInfo);

    // Sentry captures errors thrown outside render lifecycles automatically;
    // for boundary-caught errors we report them explicitly so the component
    // stack is attached.
    Sentry.captureException(error, {
      contexts: { react: { componentStack: errorInfo.componentStack } },
    });
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }
      return (
        <div
          style={{
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '24px',
            textAlign: 'center',
            fontFamily:
              '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
            color: '#1a1a2e',
            background: '#f4f6fa',
          }}
        >
          <h1 style={{ fontSize: '24px', marginBottom: '12px' }}>
            Something went wrong
          </h1>
          <p style={{ fontSize: '14px', color: '#556', marginBottom: '24px' }}>
            The page hit an unexpected error and could not finish loading. Our
            team has been notified. You can try reloading the page to recover.
          </p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            style={{
              padding: '10px 20px',
              fontSize: '14px',
              fontWeight: 600,
              color: '#fff',
              background: '#2b4a7a',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
            }}
          >
            Reload page
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
